import { getFields } from 'types';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import {
  Jobsite,
  SelfOnboardingNavigationStepKey,
  SelfOnboardingState,
  SelfOnboardingStepConfig,
  SelfOnboardingStepKey,
} from 'containers/selfOnboarding/types';

export type SequentialStepArgs = Pick<
  SelfOnboardingState,
  'shouldSelectContractor'
> & {
  jobsite: Jobsite;
  worker: Pick<SelfOnboardingState['worker'], 'isAssignedToMultipleJobsites'>;
  getStepConfig: () => SelfOnboardingStepConfig;
  /** Selects the authenticated-self-onboarding step order/visibility when true. */
  authenticatedSelfOnboardingEnabled?: boolean;
};

/** This type is used for all sequential steps including multi-instance document steps. */
export type SequentialStep =
  | SelfOnboardingNavigationStepKey
  | {
      key: SelfOnboardingNavigationStepKey;
      workerDocTypeKey: SelfOnboardingStepKey;
      isVisible: (args: SequentialStepArgs) => boolean;
    };

/** This type is used only for sequential steps which are configured. */
export type SequentialConfigStep =
  | SelfOnboardingStepKey
  | {
      key: SelfOnboardingStepKey;
      isVisible: (args: SequentialStepArgs) => boolean;
    };

const contractorSelectionStep: SequentialConfigStep = {
  key: SelfOnboardingStepKey.ContractorSelection,
  isVisible: (args): boolean => !!args?.shouldSelectContractor,
};

const loginStep: SequentialConfigStep = {
  key: SelfOnboardingStepKey.Login,
  isVisible: (args): boolean => !args?.worker,
};

// Authenticated flow only: brand-new workers set their name up front. Existing
// workers (already have a worker record) skip it in the forward flow and reach
// it only via the review screen's "Full name" edit.
const workerNameStep: SequentialConfigStep = {
  key: SelfOnboardingStepKey.WorkerName,
  isVisible: (args): boolean => !args?.worker,
};

const verifyMobileDeviceStep: SequentialConfigStep = {
  key: SelfOnboardingStepKey.VerifyMobileDevice,
  isVisible: (state): boolean => !state?.worker?.isAssignedToMultipleJobsites,
};

/**
 * Steps shared by both flows, from Basic Info through the profile photo. The
 * two flows differ only in the steps that come before (auth + contractor
 * ordering) and after (legacy end-of-flow mobile verification).
 */
const onboardingAndDocumentSteps: SequentialConfigStep[] = [
  SelfOnboardingStepKey.BasicInfo,
  SelfOnboardingStepKey.Profile,
  {
    key: SelfOnboardingStepKey.EmergencyContactInfo,
    isVisible: (args): boolean => {
      const jobsiteConfigWorkerInfo =
        getFields<EditJobsiteConfigurationFormData['workerInfo']>();
      const stepConfig = args.getStepConfig();
      return !stepConfig.fields?.[jobsiteConfigWorkerInfo.emergencyContact]
        ?.isHidden;
    },
  },
  SelfOnboardingStepKey.GovernmentIssuedId,
  SelfOnboardingStepKey.NycSiteSafetyTrainingCard,
  SelfOnboardingStepKey.OshaCard,
  SelfOnboardingStepKey.TwicCard,
  SelfOnboardingStepKey.JobsiteSafetyVideo,
  SelfOnboardingStepKey.JobsiteSafetyDocument,
  SelfOnboardingStepKey.LIRRBlueTraining,
  SelfOnboardingStepKey.MustSafetyReport,
  SelfOnboardingStepKey.ConfinedSpacesTraining,
  SelfOnboardingStepKey.ProfilePhoto,
];

/** Legacy flow: contractor selection precedes login; mobile verified at the end. */
const sequentialSteps: SequentialConfigStep[] = [
  SelfOnboardingStepKey.LanguageSelection,
  contractorSelectionStep,
  loginStep,
  ...onboardingAndDocumentSteps,
  verifyMobileDeviceStep,
  SelfOnboardingStepKey.ReviewCompletedProfile,
];

/**
 * Authenticated self-onboarding flow: phone + OTP happen up front (login moves
 * before contractor selection), and the end-of-flow mobile verification is
 * removed since the phone is already verified.
 */
const authenticatedSelfOnboardingSequentialSteps: SequentialConfigStep[] = [
  SelfOnboardingStepKey.LanguageSelection,
  loginStep,
  contractorSelectionStep,
  workerNameStep,
  ...onboardingAndDocumentSteps,
  SelfOnboardingStepKey.ReviewCompletedProfile,
];

export const getSequentialSteps = (
  authenticatedSelfOnboardingEnabled?: boolean,
): SequentialConfigStep[] =>
  authenticatedSelfOnboardingEnabled
    ? authenticatedSelfOnboardingSequentialSteps
    : sequentialSteps;

const lastSequentialStep = sequentialSteps[sequentialSteps.length - 1];
export const lastStep =
  typeof lastSequentialStep === 'string'
    ? lastSequentialStep
    : lastSequentialStep.key;

export const noOrderInfoSteps: SelfOnboardingStepKey[] = [
  SelfOnboardingStepKey.LanguageSelection,
  SelfOnboardingStepKey.ContractorSelection,
  SelfOnboardingStepKey.Login,
  SelfOnboardingStepKey.ConfirmLoginCode,
  SelfOnboardingStepKey.WorkerName,
  SelfOnboardingStepKey.BasicInfo,
  SelfOnboardingStepKey.VerifyMobileDeviceToSignIn,
  SelfOnboardingStepKey.ConfirmMobileDevice,
  SelfOnboardingStepKey.ConfirmMobileDeviceToSignIn,
  SelfOnboardingStepKey.ReviewCompletedProfile,
];

export const getNoGoBackSteps = (
  args: Omit<SequentialStepArgs, 'getStepConfig'>,
): SelfOnboardingStepKey[] => [
  // In the authenticated flow the first interactive step is always Login (phone);
  // contractor selection moves after OTP, so it's no longer the entry step.
  args.authenticatedSelfOnboardingEnabled || !args.shouldSelectContractor
    ? SelfOnboardingStepKey.Login
    : SelfOnboardingStepKey.ContractorSelection,
  SelfOnboardingStepKey.Closed,
];

type WhiteListedStepState = Pick<
  SelfOnboardingState,
  | 'didWorkerMatch'
  | 'userAccountId'
  | 'jobsiteWorker'
  | 'phoneVerificationToken'
>;
export type WhiteListedStep =
  | SelfOnboardingStepKey
  | {
      key: SelfOnboardingStepKey;
      isWhiteListed: (state: WhiteListedStepState) => boolean;
    };

function isWhiteListedIfWorkerMatched(state: WhiteListedStepState): boolean {
  return state?.didWorkerMatch;
}

function isWhiteListedIfSignedIn(state: WhiteListedStepState): boolean {
  return Boolean(state?.userAccountId);
}

function isWhiteListedIfWorkerMatchedOrSignedIn(
  state: WhiteListedStepState,
): boolean {
  return Boolean(state?.didWorkerMatch || state?.userAccountId);
}

/**
 * Authenticated flow: a step past the OTP challenge is only reachable once the
 * phone has been verified (token minted) or the worker is signed in. This is
 * what stops an unauthenticated user from URL-jumping past phone verification.
 */
function isWhiteListedIfPhoneVerifiedOrSignedIn(
  state: WhiteListedStepState,
): boolean {
  return Boolean(state?.phoneVerificationToken || state?.userAccountId);
}

const whiteListedSteps: WhiteListedStep[] = [
  SelfOnboardingStepKey.LanguageSelection,
  SelfOnboardingStepKey.ContractorSelection,
  SelfOnboardingStepKey.Login,
  SelfOnboardingStepKey.ConfirmLoginCode,
  SelfOnboardingStepKey.BasicInfo,
  SelfOnboardingStepKey.WorkerConsentDocument,
  {
    key: SelfOnboardingStepKey.AlreadyCompleted,
    isWhiteListed: isWhiteListedIfWorkerMatchedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.Closed,
    isWhiteListed: isWhiteListedIfWorkerMatchedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor,
    isWhiteListed: isWhiteListedIfSignedIn,
  },
  {
    key: SelfOnboardingStepKey.VerifyMobileDeviceToSignIn,
    isWhiteListed: isWhiteListedIfWorkerMatched,
  },
  {
    key: SelfOnboardingStepKey.ConfirmMobileDeviceToSignIn,
    isWhiteListed: isWhiteListedIfWorkerMatched,
  },
  SelfOnboardingStepKey.UnableToVerifyAccount,
];

/**
 * Authenticated flow: only the pre-auth steps (language + phone + OTP) are
 * freely reachable; everything past OTP requires a verified phone or sign-in.
 */
const authenticatedSelfOnboardingWhiteListedSteps: WhiteListedStep[] = [
  SelfOnboardingStepKey.LanguageSelection,
  SelfOnboardingStepKey.Login,
  SelfOnboardingStepKey.ConfirmLoginCode,
  {
    key: SelfOnboardingStepKey.ContractorSelection,
    isWhiteListed: isWhiteListedIfPhoneVerifiedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.WorkerName,
    isWhiteListed: isWhiteListedIfPhoneVerifiedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.BasicInfo,
    isWhiteListed: isWhiteListedIfPhoneVerifiedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.WorkerConsentDocument,
    isWhiteListed: isWhiteListedIfPhoneVerifiedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.AlreadyCompleted,
    isWhiteListed: isWhiteListedIfWorkerMatchedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.Closed,
    isWhiteListed: isWhiteListedIfWorkerMatchedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor,
    isWhiteListed: isWhiteListedIfSignedIn,
  },
  SelfOnboardingStepKey.UnableToVerifyAccount,
];

export const getWhiteListedSteps = (
  authenticatedSelfOnboardingEnabled?: boolean,
): WhiteListedStep[] =>
  authenticatedSelfOnboardingEnabled
    ? authenticatedSelfOnboardingWhiteListedSteps
    : whiteListedSteps;

export const previousSteps: Partial<
  Record<SelfOnboardingStepKey, SelfOnboardingStepKey>
> = {
  [SelfOnboardingStepKey.ConfirmLoginCode]: SelfOnboardingStepKey.Login,
  [SelfOnboardingStepKey.ConfirmMobileDevice]:
    SelfOnboardingStepKey.VerifyMobileDevice,
  [SelfOnboardingStepKey.WorkerConsentDocument]:
    SelfOnboardingStepKey.BasicInfo,
};
